/**
 * Dependencies.
 */
import { Component, Mixins } from 'vue-property-decorator';

import AddressModel from '@/api/address/address.model';
import ServicePointModel from '@/api/address/service-point.model';
import { servicePointService } from '@/api/address/service-point.service';
import { formatAddressSingleLine } from '@/modules/main/utils/address';
import { calculateDistanceBetweenTwoPoints } from '@/modules/main/utils/map';
import MapPickUpPoints from '@/modules/tracking/components/map/pick-up-points/pick-up-points.component.vue';
import {
  ORDER_REQUEST_STATUS,
  ORDER_REQUEST_TYPES
} from '@/modules/tracking/constants';
import TimelineMixin from '@/modules/tracking/mixins/timeline.mixin';
import TrackingMixin from '@/modules/tracking/mixins/tracking.mixin';
import { trackingStore } from '@/modules/tracking/store';
import configuration from '@/plugins/configuration.plugin';
import Error from '@/ui/components/error.component.vue';
import NotifyClientRequest from '@/ui/components/notify/client-request.component.vue';
import Timeline from '@/ui/components/timeline/timeline.component.vue';

/**
 * Component.
 */
@Component({
  components: {
    Error,
    MapPickUpPoints,
    NotifyClientRequest,
    Timeline
  },
  name: 'pick-up-list-scene'
})
export default class extends Mixins(TrackingMixin, TimelineMixin) {
  /**
   * Property `isLoadingServicePoints`.
   */
  isLoadingServicePoints = false;

  /**
   * Property `distributionCenterRadius`.
   */
  distributionCenterRadius = null;

  /**
   * Property `servicePoints`.
   */
  servicePoints: ServicePointModel[] = [];

  /**
   * Getter `address`.
   */
  get address() {
    const { order = null } = trackingStore || {};
    const { address = null } = order || {};

    return address;
  }

  /**
   * Getter `servicePoint`.
   */
  get servicePoint() {
    const { order = null } = trackingStore || {};
    const { address = null } = order || {};
    const { servicePoint = null } = address || {};
    const { name = null } = servicePoint || {};

    return name;
  }

  /**
   * Getter `currentAddressFormatted`.
   */
  get currentAddressFormatted() {
    const { address } = this;

    return formatAddressSingleLine({ address });
  }

  /**
   * Getter has today address request.
   */
  get hasTodayAddressRequest() {
    const { requests = [] } = trackingStore || {};
    const today = this.$moment().startOf('day');

    const todayDateRequest = requests.filter(
      ({ recordDate = null, status, type }) =>
        this.$moment(recordDate).isValid() &&
        this.$moment(recordDate)
          .startOf('day')
          .isSame(today) &&
        type === ORDER_REQUEST_TYPES.CHANGE_ADDRESS &&
        (status.code === ORDER_REQUEST_STATUS.AUTOMATIC_PENDING_TREATMENT ||
          status.code === ORDER_REQUEST_STATUS.TREATED)
    );

    return todayDateRequest.length > 0;
  }

  /**
   * Getter original marker.
   */
  get originalMarker() {
    const { latitude, longitude } = this.address || {};

    return { latitude, longitude };
  }

  /**
   * Getter `pickUpPointsMarkers`.
   */
  get pickUpPointsMarkers() {
    const pickUpPoints = this.servicePoints.map(
      ({
        code = null,
        hasDockingDay = false,
        hasRadiusDistance = false,
        hasWorkingDay = false,
        id = null,
        latitude = null,
        longitude = null,
        name = null
      }) => ({
        code,
        coordinates: [latitude, longitude],
        hasDockingDay,
        hasRadiusDistance,
        hasWorkingDay,
        id,
        name
      })
    );

    // Pick up points.
    return pickUpPoints || [];
  }

  /**
   * Getter `numberPackages`.
   */
  get numberPackages() {
    const { numberPackages = null } = trackingStore || {};

    return numberPackages;
  }

  /**
   * Getter `expectedDeliveryDate`.
   */
  get expectedDeliveryDate() {
    let expectedOrderDate = null;

    if (this.isDelivery) {
      const { expectedDeliveryDate = null } = trackingStore.delivery || {};
      expectedOrderDate = expectedDeliveryDate;
    } else {
      const { expectedCollectDate = null } = trackingStore.collect || {};
      expectedOrderDate = expectedCollectDate;
    }

    return expectedOrderDate;
  }

  /**
   * Getter `newDate`.
   */
  get newDate() {
    const { dayChanged } = trackingStore || {};

    return dayChanged || null;
  }

  /**
   * Getter `notifyErrorSettings`.
   */
  get notifyErrorSettings() {
    return {
      backgroundColor: 'var(--pk-color-error-lite)',
      displayMode: 2,
      icon: 'material-icons',
      iconColor: 'var(--pk-color-error)',
      iconText: 'warning',
      id: 'deviver-error',
      progressBarColor: 'var(--pk-color-error)',
      transitionIn: 'fadeIn',
      transitionInMobile: 'fadeIn',
      transitionOut: 'fadeOut',
      transitionOutMobile: 'fadeOut'
    } as unknown;
  }

  /**
   * Lifecycle mounted hook.
   */
  async mounted() {
    this.$on('trackingDataUpdated', async () => {
      const isValid = this.handleIsValidToBeOnScene();

      if (!isValid) {
        this.handleForceTrackingScene();
      }
    });

    await this.handleInitializeMixin();

    const isValid = this.handleIsValidToBeOnScene();

    if (!isValid) {
      this.handleForceTrackingScene();
    } else {
      await this.handleInitialize();
    }
  }

  /**
   * Handle is valid to be on scene.
   */
  handleIsValidToBeOnScene() {
    return this.canCreateChangeRequest && this.canChangeAddressToServicePoint;
  }

  /**
   * Handle initialize.
   */
  async handleInitialize() {
    this.isLoadingServicePoints = true;

    await this.handleGetConfiguration();
    await this.handleGetServicePoints();

    this.isLoadingServicePoints = false;
  }

  /**
   * Handle get configuration.
   */
  async handleGetConfiguration() {
    const config = await configuration.getDistributionCenterRadius();
    const { distributionCenterRadius = null } = config || {};

    this.distributionCenterRadius = distributionCenterRadius;
  }

  /*
   * Handle get service point by id.
   */
  async handleGetServicePointById(id) {
    let servicePoint = null;

    try {
      const { numberPackages } = this || {};
      const query = `?numberPackages=${this.isDelivery ? numberPackages : 1}`;
      const endpoint = `${servicePointService.getModelEndpoint}/${id}${query}`;

      servicePoint = await servicePointService.GET(endpoint);
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetServicePointById',
          scene: 'PickUpListScene'
        });
      }
    }

    return servicePoint;
  }

  /**
   * Handle get service points.
   */
  async handleGetServicePoints() {
    try {
      let { servicePoints = [] } = trackingStore || {};

      if (!servicePoints?.length) {
        servicePoints = await servicePointService.get();
        servicePoints = this.handleFilterServicePoints(servicePoints);
        trackingStore.setServicePoints(servicePoints);
      }

      this.servicePoints = servicePoints;
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetServicePoints',
          scene: 'PickUpListScene'
        });
      }
    } finally {
      this.handleServicePointsRestrictions();
    }
  }

  /**
   * Handle service points restrictions.
   */
  handleServicePointsRestrictions() {
    // Radius and original marker.
    const radius = this.distributionCenterRadius || null;
    const original = [
      this.originalMarker.latitude,
      this.originalMarker.longitude
    ];

    // Current day.
    const day = this.$moment().isoWeekday();
    const { hasTodayAddressRequest } = this || {};

    // Pick up points information and restrictions.
    this.servicePoints = this.servicePoints.map(item => {
      const {
        dockingDays = null,
        latitude = null,
        longitude = null,
        workDays = null
      } = item || {};

      // Has radius distance from current address.
      const distance = calculateDistanceBetweenTwoPoints({
        marker1: original,
        marker2: [latitude, longitude]
      });
      const hasRadiusDistance = distance < radius && !hasTodayAddressRequest;

      // Has docking day restriction for current day?
      let hasDockingDay = false;
      if (dockingDays && dockingDays.hasOwnProperty(day)) {
        const { isActive = false } = dockingDays[day];
        hasDockingDay = isActive;
      }

      // Has work day for current day?
      let hasWorkingDay = false;
      if (workDays && workDays.hasOwnProperty(day)) {
        const { isActive = false } = workDays[day];
        hasWorkingDay = isActive;
      }

      return {
        ...item,
        hasDockingDay,
        hasRadiusDistance,
        hasWorkingDay
      };
    });
  }

  /**
   * Handle service point address formatted.
   */
  handleServicePointAddressFormatted(address: AddressModel) {
    return formatAddressSingleLine({ address });
  }

  /**
   * Handle choose service up point.
   */
  async handleChooseServicePoint(servicePoint: ServicePointModel) {
    const { key } = this;
    const { id: servicePointId } = servicePoint || {};

    const item = await this.handleGetServicePointById(servicePointId);

    const { configs, hasPackageCapacity = false } = item || {};
    const { ServicePointAcceptCollects, ServicePointAcceptIce } = configs || {};
    const servicePointAcceptCollects = ServicePointAcceptCollects === 'true';
    const servicePointAcceptIce = ServicePointAcceptIce === 'true';

    const { hasDeliveryExpiringPackages, isCollect } = this || {};

    if (!servicePointAcceptCollects && isCollect) {
      this.$kit.notify.show(
        this.i18n('TRACKING.LABELS.ERROR'),
        this.i18n('TRACKING.ERRORS.SERVICE_POINT_DOES_NOT_ACCEPT_COLLECTS'),
        this.notifyErrorSettings
      );
    } else if (!hasPackageCapacity) {
      this.$kit.notify.show(
        this.i18n('TRACKING.LABELS.ERROR'),
        this.i18n('TRACKING.ERRORS.SERVICE_POINT_DOES_NOT_HAVE_CAPACITY'),
        this.notifyErrorSettings
      );
    } else if (!servicePointAcceptIce && hasDeliveryExpiringPackages) {
      this.$kit.notify.show(
        this.i18n('TRACKING.LABELS.ERROR'),
        this.i18n('TRACKING.ERRORS.SERVICE_POINT_DOES_NOT_ACCEPT_ICE'),
        this.notifyErrorSettings
      );
    } else {
      trackingStore.setChangeServicePoint(servicePoint);

      this.$router.push({
        name: 'PickUpConfirm',
        params: { key }
      });
    }
  }

  /**
   * Handle marker selected.
   */
  handleMarkerSelected({ id }: { id: number }) {
    const servicePoint = this.servicePoints.find(
      ({ id: servicePointId }) => servicePointId === id
    );

    this.handleChooseServicePoint(servicePoint);
  }

  /**
   * Handle go back.
   */
  handleGoBack() {
    this.$router.back();
  }
}
